import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TeamLogo from "@/components/TeamLogo";

const DEFAULT_TEAM = {
    name: "",
    division_id: "",
    nation: "",
    budget: 500000000,
    stadium_level: 0,
    logo_url: "",
};

export default function AdminTeams() {
    const acc = useAccentFn();
    const [teams, setTeams] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [editing, setEditing] = useState(null);
    const [moving, setMoving] = useState(null);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        const [t, d] = await Promise.all([api.get("/teams"), api.get("/divisions")]);
        setTeams(t.data);
        setDivisions(d.data);
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const divisionById = useMemo(() => {
        const m = {};
        divisions.forEach((d) => (m[d.id] = d));
        return m;
    }, [divisions]);

    const save = async (t) => {
        setErr("");
        try {
            if (t.id) {
                await api.put(`/teams/${t.id}`, t);
            } else {
                await api.post("/teams", t);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const moveTeam = async (team, division_id) => {
        setErr("");
        try {
            await api.post(`/teams/${team.id}/move`, { division_id });
            setMoving(null);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa squadra?")) return;
        try {
            await api.delete(`/teams/${id}`);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const visible = teams.filter(
        (t) => filter === "all" || (divisionById[t.division_id]?.code === filter)
    );

    return (
        <div data-testid="admin-teams">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">Squadre</h2>
                    <p className="op-label mt-2">{teams.length} / 56 club registrati</p>
                </div>
                <button
                    className="op-btn op-btn-primary"
                    onClick={() => setEditing({ ...DEFAULT_TEAM, division_id: divisions[0]?.id || "" })}
                    data-testid="team-add-btn"
                >
                    + Nuova squadra
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
                <button
                    onClick={() => setFilter("all")}
                    className={`op-btn ${filter === "all" ? "op-btn-primary" : ""}`}
                    data-testid="team-filter-all"
                >
                    Tutte ({teams.length})
                </button>
                {divisions.map((d) => {
                    const count = teams.filter((t) => t.division_id === d.id).length;
                    return (
                        <button
                            key={d.code}
                            onClick={() => setFilter(d.code)}
                            className={`op-btn ${filter === d.code ? "op-btn-primary" : ""}`}
                            data-testid={`team-filter-${d.code.toLowerCase()}`}
                        >
                            {d.code} ({count})
                        </button>
                    );
                })}
            </div>

            <TelegramBulkImport onDone={load} />

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4">
                    {err}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full border border-t22252a" data-testid="teams-table">
                    <thead>
                        <tr className="bg-t1a1d24 op-label">
                            <th className="text-left p-3">Squadra</th>
                            <th className="text-left p-3">Divisione</th>
                            <th className="text-left p-3">Nazione</th>
                            <th className="text-right p-3">Budget</th>
                            <th className="text-right p-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((t) => {
                            const d = divisionById[t.division_id];
                            return (
                                <tr
                                    key={t.id}
                                    className="border-t border-t22252a hover:bg-t15171c"
                                    data-testid={`team-row-${t.id}`}
                                >
                                    <td className="p-3">
                                        <div className="flex items-center gap-3">
                                            <TeamLogo team={t} accentColor={acc(d?.accent_color || "rgb(var(--tc-2bb6ff))")} size={36} />
                                            <span className="text-tffffff font-medium">{t.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-3">
                                        <span
                                            className="op-label px-2 py-1 border"
                                            style={{ color: acc(d?.accent_color), borderColor: withAlpha(acc(d?.accent_color) || "rgb(var(--tc-ffffff))", "55") }}
                                        >
                                            {d?.code || "—"}
                                        </span>
                                    </td>
                                    <td className="p-3 op-mono text-sm text-tc9ccd3">{t.nation || "—"}</td>
                                    <td className="p-3 op-mono text-right text-tc9ccd3">
                                        {(t.budget / 1000000).toLocaleString("it-IT")} mln
                                    </td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => setMoving(t)}
                                            className="op-btn py-1.5 px-3 text-xs mr-2"
                                            data-testid={`team-move-btn-${t.id}`}
                                        >
                                            Sposta
                                        </button>
                                        <button
                                            onClick={() => setEditing(t)}
                                            className="op-btn py-1.5 px-3 text-xs mr-2"
                                            data-testid={`team-edit-btn-${t.id}`}
                                        >
                                            Modifica
                                        </button>
                                        <button
                                            onClick={() => remove(t.id)}
                                            className="op-btn op-btn-ghost py-1.5 px-3 text-xs text-tff3b30 hover:text-tff3b30"
                                            data-testid={`team-delete-btn-${t.id}`}
                                        >
                                            Elimina
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={5} className="p-8 text-center op-label">
                                    Nessuna squadra in questo filtro.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <TeamEditor
                    value={editing}
                    divisions={divisions}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}

            {moving && (
                <MoveDialog
                    team={moving}
                    divisions={divisions}
                    onCancel={() => setMoving(null)}
                    onMove={moveTeam}
                />
            )}
        </div>
    );
}

function TeamEditor({ value, divisions, onCancel, onSave }) {
    const [t, setT] = useState(value);
    const upd = (k, v) => setT((p) => ({ ...p, [k]: v }));

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="team-editor">
            <div className="op-card w-full max-w-lg p-6">
                <div className="op-label text-t2bb6ff">
                    {t.id ? "// MODIFICA SQUADRA" : "// NUOVA SQUADRA"}
                </div>
                <h3 className="op-heading text-2xl mt-2">{t.name || "Nuova squadra"}</h3>
                <div className="mt-5 space-y-3">
                    <div>
                        <label className="op-label block mb-2">Nome</label>
                        <input
                            value={t.name}
                            onChange={(e) => upd("name", e.target.value)}
                            className="op-input"
                            data-testid="team-name-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="op-label block mb-2">Divisione</label>
                            <select
                                value={t.division_id}
                                onChange={(e) => upd("division_id", e.target.value)}
                                className="op-input"
                                data-testid="team-division-input"
                            >
                                {divisions.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.code} — {d.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="op-label block mb-2">Nazione</label>
                            <input
                                value={t.nation || ""}
                                onChange={(e) => upd("nation", e.target.value)}
                                className="op-input"
                                placeholder="Italia · Spagna · Francia · Inghilterra"
                                data-testid="team-nation-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="op-label block mb-2">Budget (€)</label>
                            <input
                                type="number"
                                value={t.budget}
                                onChange={(e) => upd("budget", parseFloat(e.target.value) || 0)}
                                className="op-input op-mono"
                                data-testid="team-budget-input"
                            />
                        </div>
                        <div>
                            <label className="op-label block mb-2">Livello Stadio</label>
                            <input
                                type="number"
                                value={t.stadium_level}
                                onChange={(e) => upd("stadium_level", parseInt(e.target.value) || 0)}
                                className="op-input op-mono"
                                data-testid="team-stadium-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="op-label block mb-2">Logo URL (opzionale)</label>
                        <input
                            value={t.logo_url || ""}
                            onChange={(e) => upd("logo_url", e.target.value)}
                            className="op-input"
                            placeholder="https://…"
                            data-testid="team-logo-input"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onCancel} className="op-btn" data-testid="team-cancel-btn">
                        Annulla
                    </button>
                    <button onClick={() => onSave(t)} className="op-btn op-btn-primary" data-testid="team-save-btn">
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
}

function MoveDialog({ team, divisions, onCancel, onMove }) {
    const [target, setTarget] = useState(team.division_id);
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="team-move-dialog">
            <div className="op-card w-full max-w-md p-6">
                <div className="op-label text-t2bb6ff">// PROMOZIONE / RETROCESSIONE</div>
                <h3 className="op-heading text-2xl mt-2">Sposta {team.name}</h3>
                <p className="text-t8f95a3 text-sm mt-2">
                    Cambia la divisione di appartenenza della squadra (uso a fine stagione).
                </p>
                <div className="mt-5">
                    <label className="op-label block mb-2">Divisione di destinazione</label>
                    <select
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        className="op-input"
                        data-testid="team-move-target-select"
                    >
                        {divisions.map((d) => (
                            <option key={d.id} value={d.id}>
                                {d.code} — {d.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onCancel} className="op-btn" data-testid="team-move-cancel-btn">
                        Annulla
                    </button>
                    <button
                        onClick={() => onMove(team, target)}
                        className="op-btn op-btn-primary"
                        data-testid="team-move-confirm-btn"
                    >
                        Conferma spostamento
                    </button>
                </div>
            </div>
        </div>
    );
}


const TG_TEMPLATE = `⚽️ AC Milan — Alfio D'Anna Alfio
⚽️ Alavés — Maurizio Sorrentino @radamel88
⚽️ Amiens — Giuseppe Tramontano @peppecao
⚽️ Angers — Mattia Pallotta @MattiaPallotta
⚽️ Arsenal — Davide Sibilla @Sibi1192
⚽️ AS Roma — Giuseppe Ferreri @Kill3r22
⚽️ Atalanta — Nico Benz @nicobenz
⚽️ Athletic Bilbao — Mirco Corsi @kimottina
⚽️ Atlético Madrid — Ale Rizzetto Alessandro
⚽️ Auxerre — Adriano Bello @adry_b89
⚽️ Barcelona — Remo Berardi @Remasso86
⚽️ Cádiz — Roberto de Padua @bobfolsom
⚽️ Cagliari — Betz @El_Betz
⚽️ Chelsea — Andrea Pi Andrea
⚽️ Como — Domenico Dive Dodo19
⚽️ Hellas Verona — Daryo PS @daryo_ps
⚽️ Inter — Peppe Russo @OriginalPes
⚽️ Juventus — Piero Piludu @Pierpil
⚽️ Las Palmas — Manuel Poggiolini Manuel
⚽️ Leeds United — Flavio Gulizia Flavio
⚽️ Leicester — Antonio Arena Antonio
⚽️ Lens — Michele Carrella Michele
⚽️ Levante — Fabio Di Costanzo Fabio
⚽️ Lille — Roberto Gulizia Roberto
⚽️ Liverpool — Daniele Mosca @icescreaman3
⚽️ Mallorca — Roberto Everton @RobMarioz
⚽️ Manchester City — Giulio Napoletano Giu84
⚽️ Manchester Utd — Simone Stabile @likers124
⚽️ Marseille — Alessandro Cioni Alessandro
⚽️ Millwall — Marco La Rosa Marco
⚽️ Monaco — Luigi Briatico @Flea_maiden
⚽️ Montpellier — Amedeo Lombardi @Amedeo7676
⚽️ Monza — Alessandro Arosio @Ale08522
⚽️ Napoli — Francesco Martusciello Francesco
⚽️ Newcastle United — Lorenzo Montanaro Lorenzo
⚽️ Nottingham Forest — Davide Vilardo @DavidAceVila
⚽️ Osasuna — Carlo Caputo Carlo
⚽️ Palermo — Ciccio Cucina @Ciccio_xx
⚽️ Paris FC — Emanuele Baldi @spiccih
⚽️ Paris Saint-Germain — Fabrizio Gaddi @ilgaddi
⚽️ QPR — Pablo di Palo @padip68
⚽️ Real Madrid — Lolo @rebelot20
⚽️ Real Sociedad — Beppe Rella @RbAvenza
⚽️ Red Star FC — Cristiano Ripanucci @cristianuccio84
⚽️ Rennes — Peppe Daniele giuseppe
⚽️ Sampdoria — Emanuele Mammarella @sorlele
⚽️ Sevilla — Diego Mariotti @VecchieManiere_27
⚽️ SS Lazio — Ale Lucchesi Ale
⚽️ Strasbourg — Marco Rizza @marcoriz84
⚽️ Torino — Ste Moscatelli @stefanomoscatelli
⚽️ Tottenham — Dario Lucchesi @Dariuzzzo
⚽️ Toulouse — Gabriele Gulizia Guli
⚽️ Valencia — Daniele Rigo @daniel_nine
⚽️ Villarreal — Fabio De Coro @Fabiusss77
⚽️ Wolverhampton — Andrea Marchionne @MrTipster48
⚽️ Wrexham — Andrea Catarinozzi @cheffone13`;

function TelegramBulkImport({ onDone }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(TG_TEMPLATE);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const run = async (dry_run) => {
        setBusy(true); setError(""); if (!dry_run) setResult(null);
        try {
            const { data } = await api.post("/teams/president-telegram/bulk-import", { text, dry_run });
            setResult(data);
            if (!dry_run) onDone?.();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-t22252a bg-t0e1014 p-4 mb-5" data-testid="tg-bulk-import">
            <button onClick={() => setOpen((v) => !v)} className="op-label text-t2bb6ff flex items-center gap-2" data-testid="tg-bulk-toggle">
                {open ? "▾" : "▸"} Importa contatti Telegram presidenti
            </button>
            {open && (
                <div className="mt-3 space-y-3">
                    <p className="op-mono text-[11px] text-t8f95a3 leading-relaxed">
                        Incolla l&apos;elenco (una riga per squadra: <span className="text-tffffff">Nome Squadra — Allenatore @handle</span>).
                        Viene impostato l&apos;handle SOLO dove c&apos;è la <span className="text-tffffff">@</span>. Le squadre senza @ restano invariate.
                    </p>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={10}
                        className="w-full op-mono text-xs bg-t0b0d11 border border-t22252a p-3 text-tffffff focus:border-t2bb6ff outline-none"
                        data-testid="tg-bulk-textarea"
                    />
                    <div className="flex gap-2">
                        <button onClick={() => run(true)} disabled={busy} className="op-btn disabled:opacity-40" data-testid="tg-bulk-preview">Anteprima</button>
                        <button onClick={() => run(false)} disabled={busy} className="op-btn op-btn-primary disabled:opacity-40" data-testid="tg-bulk-import-btn">Importa</button>
                    </div>
                    {error && <div className="op-mono text-[11px] text-tff3b30">{error}</div>}
                    {result && (
                        <div className="op-mono text-[11px] text-tc9ccd3 space-y-1" data-testid="tg-bulk-result">
                            <div>
                                {result.dry_run ? "Anteprima — " : "✓ Importato — "}
                                <span className="text-t7ce38b">{result.matched_count} con handle</span>
                                {" · "}<span className="text-tff7a1a">{result.no_handle_teams.length} senza @</span>
                                {result.unmatched_teams.length > 0 && <> · <span className="text-tff3b30">{result.unmatched_teams.length} non abbinate</span></>}
                            </div>
                            {result.unmatched_teams.length > 0 && (
                                <div className="text-tff3b30">Non abbinate: {result.unmatched_teams.join(", ")}</div>
                            )}
                            <div className="text-t5c6370">Senza @ (da fare a mano): {result.no_handle_teams.join(", ")}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
